import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_URL,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired - but don't auto-logout
      // Let the individual page/component handle the error
      console.warn('Auth error - token may be expired');
    }
    return Promise.reject(error);
  }
);

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// Companies
export const getCompanies = () => axios.get(`${API_URL}/api/companies`, getAuthHeader());
export const createCompany = (data) => axios.post(`${API_URL}/api/companies`, data, getAuthHeader());
export const deleteCompany = (id) => axios.delete(`${API_URL}/api/companies/${id}`, getAuthHeader());

// Contacts
export const getContacts = () => axios.get(`${API_URL}/api/contacts`, getAuthHeader());
export const createContact = (data) => axios.post(`${API_URL}/api/contacts`, data, getAuthHeader());
export const deleteContact = (id) => axios.delete(`${API_URL}/api/contacts/${id}`, getAuthHeader());
export const getContactDocuments = (contactId) => axios.get(`${API_URL}/api/contacts/${contactId}/documents`, getAuthHeader());

// Documents
export const getDocuments = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return axios.get(`${API_URL}/api/documents${queryString ? `?${queryString}` : ''}`, getAuthHeader());
};
export const getDocument = (id) => axios.get(`${API_URL}/api/documents/${id}`, getAuthHeader());
export const createDocument = (data) => axios.post(`${API_URL}/api/documents`, data, getAuthHeader());
export const uploadDocument = (file, companyId, isPrivate) => {
  const formData = new FormData();
  formData.append('file', file);
  if (companyId) formData.append('company_id', companyId);
  formData.append('is_private', isPrivate);
  return axios.post(`${API_URL}/api/documents/upload`, formData, {
    ...getAuthHeader(),
    headers: {
      ...getAuthHeader().headers,
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const updateDocumentStatus = (id, status) => 
  axios.patch(`${API_URL}/api/documents/${id}/status`, { status }, getAuthHeader());
export const updateDocument = (id, data) => 
  axios.patch(`${API_URL}/api/documents/${id}`, data, getAuthHeader());
export const deleteDocument = (id) => axios.delete(`${API_URL}/api/documents/${id}`, getAuthHeader());

// Tasks
export const getTasks = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return axios.get(`${API_URL}/api/tasks${queryString ? `?${queryString}` : ''}`, getAuthHeader());
};
export const createTask = (data) => axios.post(`${API_URL}/api/tasks`, data, getAuthHeader());
export const updateTaskStatus = (id, status) => 
  axios.patch(`${API_URL}/api/tasks/${id}/status`, { status }, getAuthHeader());
export const deleteTask = (id) => axios.delete(`${API_URL}/api/tasks/${id}`, getAuthHeader());

// Calendar
export const getCalendarEvents = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  return axios.get(`${API_URL}/api/calendar${queryString ? `?${queryString}` : ''}`, getAuthHeader());
};
export const createCalendarEvent = (data) => axios.post(`${API_URL}/api/calendar`, data, getAuthHeader());
export const deleteCalendarEvent = (id) => axios.delete(`${API_URL}/api/calendar/${id}`, getAuthHeader());

// Notifications
export const getNotifications = (unreadOnly = false) => 
  axios.get(`${API_URL}/api/notifications${unreadOnly ? '?unread_only=true' : ''}`, getAuthHeader());
export const markNotificationRead = (id) => 
  axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, getAuthHeader());
export const markAllNotificationsRead = () => 
  axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, getAuthHeader());

// Payments
export const createPaymentCheckout = (documentId, amount) => 
  axios.post(`${API_URL}/api/payments/create-checkout`, { document_id: documentId, amount }, getAuthHeader());
export const getPaymentStatus = (sessionId) => 
  axios.get(`${API_URL}/api/payments/status/${sessionId}`, getAuthHeader());

// AI
export const generateAIEmail = (documentId, templateType, options = {}) => 
  axios.post(`${API_URL}/api/ai/generate-email`, { 
    document_id: documentId, 
    template_type: templateType,
    ...options 
  }, getAuthHeader());

// Email
export const sendEmail = (toEmail, toName, subject, bodyHtml) => 
  axios.post(`${API_URL}/api/email/send`, { 
    to_email: toEmail, 
    to_name: toName, 
    subject, 
    body_html: bodyHtml 
  }, getAuthHeader());

// Dashboard
export const getDashboardStats = () => axios.get(`${API_URL}/api/dashboard/stats`, getAuthHeader());
